import {
	type BxpWebOperationsCreateTeamRequest as CreateTeamRequest,
	type BxpWebOperationsDiagnostics,
	type BxpWebOperationsEligibilityRequest as EligibilityRequest,
	type BxpWebOperationsEnableRequest as EnableRequest,
	type BxpWebOperationsMembershipRequest as MembershipRequest,
	type BxpWebOperationsMethods as OperationsMethods,
	type BxpWebOperationsRequest as OperationsRequest,
	type BxpWebOperationsStatusRequest as StatusRequest,
} from './bxp-web-operations-client.types';
import {
	BxpWebOperationsError,
	type BxpWebOperationsErrorKind,
	type BxpWebOperationsErrorOptions,
} from './bxp-web-operations-error';

type OperationsFetch = (path: string, init: RequestInit) => Promise<Response>;
type JsonResponse = { body: Record<string, unknown>; diagnostics: BxpWebOperationsDiagnostics };
type KnownIds = Pick<BxpWebOperationsErrorOptions, 'teamId' | 'asyncRequestId'>;
type FailureOptions = {
	ids?: KnownIds;
	errorCodes?: readonly string[];
	mutationOutcome?: BxpWebOperationsErrorOptions['mutationOutcome'];
};
type OperationFailure = (
	kind: BxpWebOperationsErrorKind,
	diagnostics?: BxpWebOperationsDiagnostics,
	options?: FailureOptions,
) => never;
type RequestOptions = {
	signal?: AbortSignal;
	body?: unknown;
	readIds?: (body: Record<string, unknown>) => KnownIds;
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);
const isNonEmptyString = (value: unknown): value is string =>
	typeof value === 'string' && value.trim().length > 0;
const nonEmptyOrUndefined = (value: unknown): string | undefined =>
	isNonEmptyString(value) ? value : undefined;
const getField = (value: unknown, ...path: string[]): unknown =>
	path.reduce<unknown>((current, key) => (isRecord(current) ? current[key] : undefined), value);
const present = (value: unknown): boolean => value !== null && value !== undefined;
const hasErrors = (value: unknown): boolean =>
	Array.isArray(value) ? value.length > 0 : present(value);
const header = (response: Response, name: string): string | undefined =>
	response.headers.get(name) ?? undefined;
const responseDiagnostics = (response: Response, body?: unknown): BxpWebOperationsDiagnostics => ({
	httpStatus: response.status,
	traceId: header(response, 'atl-traceid') ?? header(response, 'x-trace-id'),
	requestId:
		nonEmptyOrUndefined(getField(body, 'requestId')) ??
		nonEmptyOrUndefined(getField(body, 'extensions', 'requestId')) ??
		header(response, 'x-request-id'),
});
const responseErrorKind = (response: Response): BxpWebOperationsErrorKind => {
	if (response.ok) return 'malformed-response';
	if (response.status === 401 || response.status === 403) return 'permission';
	return 'http';
};
const isPathSegment = (value: unknown): value is string =>
	typeof value === 'string' && /^[a-zA-Z0-9_-]+$/.test(value);
const isAccountId = (value: unknown): value is string =>
	typeof value === 'string' && /^[a-zA-Z0-9_:-]+$/.test(value);

const MAX_MEMBERSHIP_PAGES = 20;
const MEMBERSHIP_PAGE_SIZE = 50;

const parsePlatformTeamId = (id: string): string | undefined => {
	const match = /^ari:cloud:(?:identity|teams)::team\/([a-zA-Z0-9_-]+)$/.exec(id);
	return match?.[1] ?? (isPathSegment(id) ? id : undefined);
};

const readMembershipPage = (team: unknown) => {
	const members = getField(team, 'members');
	const edges = getField(members, 'edges');
	const hasNextPage = getField(members, 'pageInfo', 'hasNextPage');
	if (
		!Array.isArray(edges) ||
		typeof hasNextPage !== 'boolean' ||
		edges.some(
			(edge) =>
				!isNonEmptyString(getField(edge, 'node', 'member', 'id')) ||
				!isNonEmptyString(getField(edge, 'node', 'state')),
		)
	) {
		return undefined;
	}
	return { edges, hasNextPage, endCursor: getField(members, 'pageInfo', 'endCursor') };
};

const CREATE_TEAM = `
	mutation WacOperationsCreateTeam($input: TeamCreateTeamInput!) {
		team {
			createTeam(input: $input) @optIn(to: "Team-crud") {
				success
				errors { extensions { errorType } }
				team { id }
			}
		}
	}
`;
const TEAM_MEMBERSHIP = `
	query WacOperationsTeamMembership($teamId: ID!, $siteId: String!, $after: String) {
		team {
			teamV2(id: $teamId, siteId: $siteId) @optIn(to: "Team-v2") {
				id
				members(state: [FULL_MEMBER], first: ${MEMBERSHIP_PAGE_SIZE}, after: $after) {
					edges { node { member { id } state role } }
					pageInfo { hasNextPage endCursor }
				}
			}
		}
	}
`;
const OWNING_ORG = `
	query WacOperationsOwningOrg($cloudId: ID!) {
		tenantContexts(cloudIds: [$cloudId]) { orgId }
	}
`;

/** Converts an identity/teams team ARI or raw team ID to the raw platformTeamId used by JSM Ops endpoints. */
export function getBxpWebOperationsPlatformTeamId(teamId: string): string {
	const platformTeamId = parsePlatformTeamId(teamId);
	if (platformTeamId) return platformTeamId;
	throw new BxpWebOperationsError({
		operation: 'team-identity',
		kind: 'invalid-input',
		mutationOutcome: 'not-started',
		teamId,
	});
}

/** Uses the existing WAC Stargate transport. Mutations are always single-attempt. */
export class BxpWebOperationsClient implements OperationsMethods {
	public constructor(private readonly fetch: OperationsFetch) {}

	private failFor({
		operation,
		mutation = false,
		ids = {},
	}: {
		operation: string;
		mutation?: boolean;
		ids?: KnownIds;
	}): OperationFailure {
		return (kind, diagnostics, options = {}) => {
			throw new BxpWebOperationsError({
				operation,
				kind,
				mutationOutcome:
					options.mutationOutcome ??
					(mutation && kind !== 'invalid-input' ? 'unknown' : 'not-started'),
				diagnostics,
				...ids,
				...options.ids,
				errorCodes: options.errorCodes,
			});
		};
	}

	private validate(fail: OperationFailure, ...values: string[]): void {
		if (values.some((value) => !isPathSegment(value))) {
			return fail('invalid-input');
		}
	}

	private async request(
		fail: OperationFailure,
		path: string,
		options: RequestOptions = {},
	): Promise<JsonResponse> {
		const { signal, body, readIds } = options;
		let response: Response;
		try {
			response = await this.fetch(path, {
				method: body === undefined ? 'GET' : 'POST',
				credentials: 'include',
				redirect: 'error',
				headers: {
					'web-platform': 'bxp-web-platform',
					...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
				},
				body: JSON.stringify(body),
				signal,
			});
		} catch (error) {
			const aborted = signal?.aborted || getField(error, 'name') === 'AbortError';
			return fail(aborted ? 'aborted' : 'network');
		}
		// Decode the response once before reading diagnostics and resource IDs.
		let json: unknown;
		try {
			json = await response.json();
		} catch {
			return fail(responseErrorKind(response), responseDiagnostics(response));
		}
		const result: JsonResponse = {
			body: isRecord(json) ? json : {},
			diagnostics: responseDiagnostics(response, json),
		};
		if (!response.ok) {
			return fail(responseErrorKind(response), result.diagnostics, { ids: readIds?.(result.body) });
		}
		if (!isRecord(json)) return fail('malformed-response', result.diagnostics);
		return result;
	}

	private graphqlErrors(fail: OperationFailure, result: JsonResponse, ids: KnownIds = {}): void {
		const errors = result.body['errors'];
		if (!present(errors)) return;
		if (!Array.isArray(errors)) return fail('malformed-response', result.diagnostics, { ids });
		if (errors.length === 0) return;
		const codes = errors
			.map((error) => getField(error, 'extensions', 'code'))
			.filter(isNonEmptyString);
		const kind = codes.some((code) => code === 'FORBIDDEN' || code === 'UNAUTHORIZED')
			? 'permission'
			: 'graphql';
		return fail(kind, result.diagnostics, { ids, errorCodes: codes });
	}

	public async getOperationsOwningOrg({ cloudId, signal }: OperationsRequest) {
		const fail = this.failFor({ operation: 'owning-organization' });
		this.validate(fail, cloudId);
		const result = await this.request(fail, '/graphql', {
			signal,
			body: { query: OWNING_ORG, variables: { cloudId } },
		});
		this.graphqlErrors(fail, result);
		const contexts = getField(result.body['data'], 'tenantContexts');
		if (!Array.isArray(contexts) || contexts.length === 0)
			return fail('malformed-response', result.diagnostics);
		const orgIds = contexts.map((context) => getField(context, 'orgId'));
		if (!orgIds.every(isPathSegment)) return fail('malformed-response', result.diagnostics);
		const [orgId] = orgIds;
		if (new Set(orgIds).size !== 1 || !orgId)
			return fail('ambiguous-organization', result.diagnostics);
		return { orgId, diagnostics: result.diagnostics };
	}

	public async createOperationsTeam({
		cloudId,
		accountId,
		displayName,
		signal,
	}: CreateTeamRequest) {
		const fail = this.failFor({ operation: 'create-team', mutation: true });
		this.validate(fail, cloudId);
		if (!isAccountId(accountId)) return fail('invalid-input');
		if (!isNonEmptyString(displayName)) return fail('invalid-input');
		// Reads the created team ID, including from failed HTTP responses, so callers can reconcile.
		const readIds = (body: Record<string, unknown>): KnownIds => ({
			teamId: nonEmptyOrUndefined(getField(body, 'data', 'team', 'createTeam', 'team', 'id')),
		});
		const result = await this.request(fail, '/graphql', {
			signal,
			readIds,
			body: {
				query: CREATE_TEAM,
				variables: {
					input: {
						displayName,
						description: '',
						membershipSettings: 'OPEN',
						scopeId: `ari:cloud:platform::site/${cloudId}`,
						members: [`ari:cloud:identity::user/${accountId}`],
					},
				},
			},
		});
		const payload = getField(result.body, 'data', 'team', 'createTeam');
		const ids = readIds(result.body);
		this.graphqlErrors(fail, result, ids);
		const errors = getField(payload, 'errors');
		if (present(errors) && !Array.isArray(errors)) {
			return fail('malformed-response', result.diagnostics, { ids });
		}
		if ((Array.isArray(errors) && errors.length > 0) || getField(payload, 'success') === false) {
			const codes = Array.isArray(errors)
				? errors.map((error) => getField(error, 'extensions', 'errorType')).filter(isNonEmptyString)
				: [];
			return fail('payload', result.diagnostics, {
				mutationOutcome: ids.teamId ? 'unknown' : 'failed',
				ids,
				errorCodes: codes,
			});
		}
		if (getField(payload, 'success') !== true || !ids.teamId) {
			return fail('malformed-response', result.diagnostics, { ids });
		}
		const platformTeamId = parsePlatformTeamId(ids.teamId);
		if (!platformTeamId) return fail('malformed-response', result.diagnostics, { ids });
		return { teamId: ids.teamId, platformTeamId, diagnostics: result.diagnostics };
	}

	public async getOperationsTeamMembership({
		cloudId,
		teamId,
		accountId,
		signal,
	}: MembershipRequest) {
		const fail = this.failFor({ operation: 'team-membership', ids: { teamId } });
		this.validate(fail, cloudId);
		if (!isAccountId(accountId)) return fail('invalid-input');
		const platformTeamId = parsePlatformTeamId(teamId);
		if (!platformTeamId) return fail('invalid-input');
		let after: string | undefined;
		let lastDiagnostics: BxpWebOperationsDiagnostics | undefined;
		const cursors = new Set<string>();
		for (let page = 0; page < MAX_MEMBERSHIP_PAGES; page++) {
			const result = await this.request(fail, '/graphql', {
				signal,
				body: { query: TEAM_MEMBERSHIP, variables: { teamId, siteId: cloudId, after } },
			});
			lastDiagnostics = result.diagnostics;
			this.graphqlErrors(fail, result);
			const team = getField(result.body, 'data', 'team', 'teamV2');
			if (!present(team)) return fail('membership-pending', result.diagnostics);
			const returnedId = getField(team, 'id');
			if (!isNonEmptyString(returnedId) || parsePlatformTeamId(returnedId) !== platformTeamId)
				return fail('malformed-response', result.diagnostics);
			const membership = readMembershipPage(team);
			if (!membership) return fail('malformed-response', result.diagnostics);
			const { edges, hasNextPage, endCursor } = membership;
			const creatorFullMember = edges.some(
				(edge) =>
					getField(edge, 'node', 'member', 'id') === `ari:cloud:identity::user/${accountId}` &&
					getField(edge, 'node', 'state') === 'FULL_MEMBER',
			);
			if (creatorFullMember || !hasNextPage) {
				return { teamId, accountId, creatorFullMember, diagnostics: result.diagnostics };
			}
			if (!isNonEmptyString(endCursor) || cursors.has(endCursor)) {
				return fail('malformed-response', result.diagnostics);
			}
			cursors.add(endCursor);
			after = endCursor;
		}
		return fail('membership-pending', lastDiagnostics);
	}

	public async getOperationsEligibility({ cloudId, teamId, teamName, signal }: EligibilityRequest) {
		const fail = this.failFor({ operation: 'eligibility', ids: { teamId } });
		this.validate(fail, cloudId);
		if (!isNonEmptyString(teamName)) return fail('invalid-input');
		const platformTeamId = parsePlatformTeamId(teamId);
		if (!platformTeamId) return fail('invalid-input');
		const result = await this.request(fail, `/jsm/ops/web/${cloudId}/v1/teams/can-enable-ops`, {
			signal,
			body: { platformTeamId, teamName },
		});
		const hasEnableOpsRight = result.body['hasEnableOpsRight'];
		const hasTeamDashboardRight = result.body['hasTeamDashboardRight'];
		if (typeof hasEnableOpsRight !== 'boolean' || typeof hasTeamDashboardRight !== 'boolean') {
			return fail('malformed-response', result.diagnostics);
		}
		return { hasEnableOpsRight, hasTeamDashboardRight, diagnostics: result.diagnostics };
	}

	public async enableOperations({
		cloudId,
		teamId,
		adminAccountIds,
		inviteUsernames,
		signal,
	}: EnableRequest) {
		const fail = this.failFor({ operation: 'enable', mutation: true, ids: { teamId } });
		this.validate(fail, cloudId);
		const platformTeamId = parsePlatformTeamId(teamId);
		if (!platformTeamId) return fail('invalid-input');
		const readIds = (body: Record<string, unknown>): KnownIds => ({
			asyncRequestId: isPathSegment(body['asyncRequestId']) ? body['asyncRequestId'] : undefined,
		});
		const result = await this.request(fail, `/jsm/ops/web/${cloudId}/v1/teams/enable-ops`, {
			signal,
			readIds,
			body: {
				platformTeamId,
				adminAccountIds,
				inviteUsernames,
			},
		});
		const { asyncRequestId } = readIds(result.body);
		if (
			hasErrors(result.body['error']) ||
			hasErrors(result.body['errors']) ||
			result.body['success'] === false
		) {
			return fail('payload', result.diagnostics, {
				ids: { asyncRequestId },
				mutationOutcome: asyncRequestId ? 'unknown' : 'failed',
			});
		}
		if (!isPathSegment(asyncRequestId)) return fail('malformed-response', result.diagnostics);
		return { asyncRequestId, diagnostics: result.diagnostics };
	}

	/** One status read; orchestration owns timers, retry limits and terminal handling. */
	public async getOperationsRequestStatus({
		cloudId,
		asyncRequestId,
		signal,
	}: StatusRequest): ReturnType<OperationsMethods['getOperationsRequestStatus']> {
		const fail = this.failFor({ operation: 'request-status', ids: { asyncRequestId } });
		this.validate(fail, cloudId, asyncRequestId);
		const result = await this.request(
			fail,
			`/jsm/ops/web/${cloudId}/v1/teams/requests/${asyncRequestId}`,
			{
				signal,
			},
		);
		const status = result.body['status'];
		if (status !== 'successful' && status !== 'in-progress' && status !== 'failed') {
			return fail('malformed-response', result.diagnostics);
		}
		return { status, diagnostics: result.diagnostics };
	}
}
